import { useState } from 'react';
import { useMemos, Memo, MEMO_CATEGORIES, MemoCategory } from '@/features/memos/hooks/useMemos';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Search, Plus, Pencil, Trash2, Pin, PinOff, StickyNote, Tag } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { motion } from 'framer-motion';

const CATEGORY_LABELS: Record<string, string> = {
  general: 'Général',
  hygiene: 'Hygiène',
  preparation: 'Préparation',
  procedure: 'Procédure',
  securite: 'Sécurité',
  materiel: 'Matériel',
};

const CATEGORY_COLORS: Record<string, string> = {
  general: 'bg-muted-foreground/20 text-muted-foreground',
  hygiene: 'bg-primary/15 text-primary',
  preparation: 'bg-warning/15 text-warning',
  procedure: 'bg-success/15 text-success',
  securite: 'bg-destructive/15 text-destructive',
  materiel: 'bg-accent/15 text-accent',
};

type FormData = { title: string; content: string; category: string };
const emptyForm: FormData = { title: '', content: '', category: 'general' };

export default function MemosPage() {
  const { memos, isLoading, createMemo, updateMemo, deleteMemo } = useMemos();
  const { toast } = useToast();

  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState<string>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Memo | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);

  const filtered = memos
    .filter(m => filterCat === 'all' || m.category === filterCat)
    .filter(m => [m.title, m.content].some(v => v.toLowerCase().includes(search.toLowerCase())));

  const openNew = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (m: Memo) => {
    setEditing(m);
    setForm({ title: m.title, content: m.content, category: m.category });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    try {
      if (editing) {
        await updateMemo.mutateAsync({ id: editing.id, ...form });
      } else {
        await createMemo.mutateAsync(form);
      }
      setDialogOpen(false);
      toast({ title: 'Enregistré', description: '✓' });
    } catch {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const handleTogglePin = async (m: Memo) => {
    await updateMemo.mutateAsync({ id: m.id, pinned: !m.pinned });
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMemo.mutateAsync(id);
      toast({ title: 'Supprimé', description: '✓' });
    } catch {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-display text-foreground">Mémos</h1>
          <p className="text-muted-foreground mt-1">Notes, protocoles et checklists personnalisés</p>
        </div>
        <Button onClick={openNew} className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground">
          <Plus size={16} /> Nouveau mémo
        </Button>
      </motion.div>

      {/* Filters */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-secondary/50 border-border/50 h-11" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <Button variant={filterCat === 'all' ? 'default' : 'secondary'} size="sm" onClick={() => setFilterCat('all')} className="rounded-full text-xs h-9">
            Tous
          </Button>
          {MEMO_CATEGORIES.map(cat => (
            <Button key={cat} variant={filterCat === cat ? 'default' : 'secondary'} size="sm" onClick={() => setFilterCat(cat)} className="rounded-full text-xs h-9">
              {CATEGORY_LABELS[cat]}
            </Button>
          ))}
        </div>
      </motion.div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-48 rounded-2xl bg-secondary/50" />)}
        </div>
      ) : filtered.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20 text-muted-foreground">
          <StickyNote size={56} className="mx-auto mb-4 opacity-20" />
          <p className="text-lg">Aucun mémo trouvé</p>
          <Button onClick={openNew} variant="secondary" className="mt-4 gap-2"><Plus size={16} /> Créer un mémo</Button>
        </motion.div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((memo, i) => (
            <motion.div
              key={memo.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className={`glass-card-hover p-5 space-y-3 group relative ${memo.pinned ? 'ring-1 ring-primary/30' : ''}`}
            >
              {memo.pinned && (
                <div className="absolute top-3 right-3">
                  <Pin size={14} className="text-primary" />
                </div>
              )}
              <div className="flex items-start justify-between">
                <h3 className="font-semibold text-foreground text-base pr-6">{memo.title}</h3>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-4 whitespace-pre-wrap">{memo.content}</p>
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium ${CATEGORY_COLORS[memo.category] || CATEGORY_COLORS.general}`}>
                  <Tag size={10} /> {CATEGORY_LABELS[memo.category] || memo.category}
                </span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => handleTogglePin(memo)}>
                    {memo.pinned ? <PinOff size={13} /> : <Pin size={13} />}
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={() => openEdit(memo)}>
                    <Pencil size={13} />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => handleDelete(memo.id)}>
                    <Trash2 size={13} />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg glass-card border-border/50">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? 'Modifier le mémo' : 'Nouveau mémo'}</DialogTitle>
            <DialogDescription className="sr-only">Formulaire de mémo</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-muted-foreground text-xs">Titre *</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="bg-secondary/50 border-border/50" placeholder="Ex: Protocole de lavage des mains" />
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">Catégorie</Label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger className="bg-secondary/50 border-border/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MEMO_CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{CATEGORY_LABELS[cat]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">Contenu</Label>
              <Textarea
                value={form.content}
                onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                className="bg-secondary/50 border-border/50 min-h-[200px] resize-y"
                placeholder="Écrivez votre mémo ici... Vous pouvez inclure des étapes, checklists, notes importantes..."
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border/50">Annuler</Button>
            <Button onClick={handleSave} disabled={!form.title.trim()} className="bg-primary hover:bg-primary/90 text-primary-foreground">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
